import computeHistogramInWorker from 'app/components/ImageHistogramDisplay/computeHistogramInWorker';
import { DisplayableImageData } from 'app/interfaces/Image';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Checkbox, Icon, Label } from 'semantic-ui-react';

type Props = {
  // Image width and height to validate the buffer length (optional but recommended)
  // Number of histogram bins to draw (default 256)
  bins?: number;
  // Provide min/max to override automatic range detection
  min?: number;
  max?: number;
  // Optional CSS className/style for the canvas element
  className?: string;
  style?: React.CSSProperties;
  // Color for histogram bars
  barColor?: string;
  // Background color for canvas
  backgroundColor?: string;
} & {
  image: DisplayableImageData;
  crop?: [number, number, number, number];
};

export default function ImageHistogramDisplay({
  bins = 256,
  min,
  max,
  className,
  style,
  barColor = '#ffffffff',
  backgroundColor = '#696969ff',
  image,
  crop,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [hist, setHist] = useState<Uint32Array>(undefined);
  const [isHistogramComputation, setIsHistogramComputation] = useState<boolean>(false);
  const [internalCrop, setInternalCrop] = useState<[number, number, number, number]>(undefined);
  const [isLogScale, setIsLogScale] = useState<boolean>(false);

  if (!_.isEqual(crop, internalCrop)) {
    setInternalCrop(crop);
  }

  useEffect(() => {
    // Prepare input buffer
    let buffer = image?.getPixelData() as Uint16Array;
    if (!buffer) return;

    setIsHistogramComputation(true);
    console.time('Computing histogram...');
    const histogramComputationPromise = computeHistogramInWorker(
      buffer,
      Math.max(1, Math.floor(bins)),
      min,
      max,
      image.width,
      image.height,
      internalCrop
    );
    histogramComputationPromise
      .then((histogram) => {
        setHist(histogram);
      })
      .finally(() => {
        console.timeEnd('Computing histogram...');
        setIsHistogramComputation(false);
      });

    return () => {
      histogramComputationPromise.cancel();
    };
  }, [image, internalCrop, bins, min, max]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!hist || !canvas) return;

    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const cssWidth = style?.width;
    const cssHeight = style?.height;

    const resolvedCssWidth =
      typeof cssWidth === 'number' && cssWidth > 0 ? cssWidth : canvas.clientWidth || 400;
    const resolvedCssHeight =
      typeof cssHeight === 'number' && cssHeight > 0 ? cssHeight : canvas.clientHeight || 100;

    canvas.width = Math.round(resolvedCssWidth * dpr);
    canvas.height = Math.round(resolvedCssHeight * dpr);
    canvas.style.width = `${resolvedCssWidth}px`;
    canvas.style.height = `${resolvedCssHeight}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);

    // Clear & background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, resolvedCssWidth, resolvedCssHeight);

    // Find max count for vertical scaling
    let maxCount = 0;
    for (let i = 0; i < bins; i++) {
      const value = isLogScale ? Math.log(hist[i] + 1) : hist[i];
      if (value > maxCount) {
        maxCount = value;
      }
    }
    if (maxCount === 0) maxCount = 1;

    // Draw bars
    const padding = 4;
    const w = resolvedCssWidth - padding * 2;
    const h = resolvedCssHeight - padding * 2;
    const barW = w / bins;

    ctx.translate(padding, padding);
    ctx.fillStyle = barColor;
    for (let i = 0; i < bins; i++) {
      const value = isLogScale ? Math.log(hist[i] + 1) : hist[i];
      const heightPx = (value / maxCount) * h;
      ctx.fillRect(i * barW, h - heightPx, Math.max(1, Math.ceil(barW)), heightPx);
    }
    // reset transform for future draws
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, [hist, barColor, backgroundColor, style, isLogScale]);

  // The canvas will size from style.width/style.height when provided, or defaults used in drawing.
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        backgroundColor,
        ...style,
        height: '100%',
      }}
    >
      {(isHistogramComputation || !image) && (
        <Label corner="left" color="black">
          <Icon name="cog" loading />
        </Label>
      )}
      <canvas
        ref={canvasRef}
        className={className}
        style={{
          display: 'block',
          width: style?.width ?? 400,
          height: style?.height ?? 100,
          ...style,
        }}
        aria-label="Image histogram"
      />
      <Checkbox
        checked={isLogScale}
        style={{ paddingLeft: '1em' }}
        onChange={(e, { checked }) => setIsLogScale(checked)}
        label={
          <label>
            <FormattedMessage id="histogram_display.isLogScale" />
          </label>
        }
      />
    </div>
  );
}
